
import React, { useState, useEffect, useMemo } from 'react';
import { storage } from '../lib/storage';
import { Order, Product, Category, SellingModel } from '../types';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Users, 
  ShoppingBag, 
  DollarSign, 
  BarChart3, 
  PieChart as PieIcon,
  Calendar,
  ArrowRight,
  Zap,
  Award,
  Map as MapIcon,
  Globe,
  ExternalLink,
  MousePointerClick,
  Share2,
  Heart,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { affiliateService } from '../services/affiliateService';
import { wishlistService } from '../services/wishlistService';

interface AdminAnalyticsProps {
  storeId: string;
}

const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ storeId }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [affiliateStats, setAffiliateStats] = useState<{ totalClicks: number, byProduct: any[] }>({ totalClicks: 0, byProduct: [] });
  const [wishlistStats, setWishlistStats] = useState<any[]>([]);

  useEffect(() => {
    setOrders(storage.getOrders().filter(o => o.storeId === storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
    setCategories(storage.getCategories().filter(c => c.storeId === storeId));
    setAffiliateStats(affiliateService.getClickStats(storeId));
    setWishlistStats(wishlistService.getStoreWishlistStats(storeId).slice(0, 5));
  }, [storeId]);

  const metrics = useMemo(() => {
    const validOrders = orders.filter(o => o.status !== 'CANCELLED');
    const totalRev = validOrders.reduce((sum, o) => sum + o.total, 0);
    const aov = validOrders.length > 0 ? totalRev / validOrders.length : 0;
    
    const catPerf = categories.map(cat => {
      const catProducts = products.filter(p => p.categoryId === cat.id);
      const catSales = validOrders.reduce((sum, order) => {
        return sum + (order.total * (catProducts.length / (products.length || 1)));
      }, 0);
      return { name: cat.name, revenue: catSales };
    }).sort((a, b) => b.revenue - a.revenue);

    return { totalRev, aov, catPerf, orderCount: validOrders.length };
  }, [orders, products, categories]);

  const salesHotspots = [
    { name: 'Mumbai, IN', count: 12, top: '65%', left: '72%' },
    { name: 'New York, US', count: 8, top: '35%', left: '25%' },
    { name: 'London, UK', count: 5, top: '30%', left: '48%' },
    { name: 'Tokyo, JP', count: 15, top: '40%', left: '88%' },
  ];

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Business Intelligence</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Lynqar Market Intelligence Platform</p>
        </div>
        <div className="flex gap-3 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
           {['7D', '1M', '3M', '1Y'].map(period => (
             <button key={period} className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${period === '7D' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-900'}`}>
               {period}
             </button>
           ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center"><Target className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Avg. Order Value (AOV)</p>
               <h3 className="text-4xl font-black text-gray-900 tracking-tighter">${metrics.aov.toLocaleString()}</h3>
            </div>
         </div>
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4">
            <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center"><MousePointerClick className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Affiliate Clicks</p>
               <h3 className="text-4xl font-black text-gray-900 tracking-tighter">{affiliateStats.totalClicks}</h3>
            </div>
         </div>
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4">
            <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center"><Heart className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Total Wishlists</p>
               <h3 className="text-4xl font-black text-gray-900 tracking-tighter">{wishlistStats.reduce((sum, item) => sum + (item.wishlistCount || 0), 0)}</h3>
            </div>
         </div>
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4">
            <div className="w-14 h-14 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center"><Zap className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Demand Conversion</p>
               <h3 className="text-4xl font-black text-gray-900 tracking-tighter">18.5%</h3>
            </div>
         </div>
      </div>

      {/* Wishlist Intelligence Hub */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 bg-white p-10 rounded-[40px] border border-gray-100 shadow-2xl space-y-8">
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-500 text-white rounded-2xl shadow-lg shadow-red-200">
                     <Heart className="h-6 w-6 fill-current" />
                  </div>
                  <div>
                     <h3 className="text-xl font-black text-gray-900 tracking-tight">Wishlist Radar</h3>
                     <p className="text-[10px] text-red-600 font-black uppercase tracking-widest">Unmet Customer Demand</p>
                  </div>
               </div>
               <span className="text-[10px] font-black text-gray-400 uppercase">High Priority</span>
            </div>

            <div className="space-y-4">
               {wishlistStats.length === 0 ? (
                 <div className="py-10 text-center opacity-30">No wishlist activity tracked.</div>
               ) : (
                 wishlistStats.map((item, idx) => (
                   <div key={item.id} className="flex items-center justify-between p-6 bg-gray-50 rounded-[2rem] border border-gray-100 hover:border-red-200 transition-all group">
                      <div className="flex items-center gap-6">
                         <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-sm">
                            <img src={item.images[0]} className="w-full h-full object-cover" alt="" />
                         </div>
                         <div>
                            <h4 className="font-black text-gray-900">{item.name}</h4>
                            <div className="flex items-center gap-3 mt-1">
                               <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${item.stock === 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                  {item.stock === 0 ? 'Out of Stock' : `${item.stock} in stock`}
                               </span>
                               <span className="text-[8px] text-gray-400 font-bold uppercase">Ref: {item.id}</span>
                            </div>
                         </div>
                      </div>
                      <div className="text-right">
                         <p className="text-2xl font-black text-gray-900">{item.wishlistCount}</p>
                         <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Potential Buyers</p>
                      </div>
                   </div>
                 ))
               )}
            </div>
         </div>

         <div className="space-y-8">
            <div className="bg-red-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Sparkles className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">AI Restock Strategy</h3>
                  <div className="p-6 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 space-y-4">
                     <AlertCircle className="h-6 w-6 text-red-200" />
                     <p className="text-xs font-bold leading-relaxed">
                        Bhai, check current data! 3 products have wishlists exceeding stock levels. Restock "Lynq Phone X1" to capture ~$5k in pending demand.
                     </p>
                  </div>
                  <button className="w-full py-4 bg-white text-red-600 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-gray-50 transition-all">
                     View Demand Report
                  </button>
               </div>
            </div>

            <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-6">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Engagement Pulse</h4>
               <div className="space-y-4">
                  {[
                    { label: 'Saved to Wishlist', val: '64%', color: 'bg-red-500' },
                    { label: 'Added to Cart', val: '42%', color: 'bg-blue-500' },
                    { label: 'Viewed Item', val: '98%', color: 'bg-emerald-500' }
                  ].map((p, i) => (
                    <div key={i} className="space-y-1.5">
                       <div className="flex justify-between items-end">
                          <p className="text-xs font-bold text-gray-700">{p.label}</p>
                          <p className="text-xs font-black text-gray-900">{p.val}</p>
                       </div>
                       <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full ${p.color}`} style={{ width: p.val }}></div>
                       </div>
                    </div>
                  ))}
               </div>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
         {/* Affiliate Performance Section */}
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-2xl space-y-8">
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg shadow-amber-200">
                     <Share2 className="h-6 w-6" />
                  </div>
                  <div>
                     <h3 className="text-xl font-black text-gray-900 tracking-tight">Affiliate Traffic</h3>
                     <p className="text-[10px] text-amber-600 font-black uppercase tracking-widest">Partner Network Performance</p>
                  </div>
               </div>
               <span className="text-[10px] font-black text-gray-400 uppercase">Top Click-thru</span>
            </div>

            <div className="space-y-4">
               {affiliateStats.byProduct.length === 0 ? (
                 <div className="py-10 text-center text-gray-400 text-xs font-bold uppercase tracking-widest opacity-30">No affiliate clicks recorded.</div>
               ) : (
                 affiliateStats.byProduct.map(stat => (
                   <div key={stat.productId} className="flex items-center justify-between p-6 bg-gray-50 rounded-[2rem] border border-gray-100 group hover:border-amber-200 transition-all">
                      <div className="flex items-center gap-4">
                         <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-amber-600 shadow-sm border border-gray-100">
                            <ExternalLink className="h-5 w-5" />
                         </div>
                         <p className="text-sm font-black text-gray-900">{stat.productName}</p>
                      </div>
                      <div className="text-right">
                         <p className="text-xl font-black text-gray-900">{stat.clickCount}</p>
                         <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Clicks logged</p>
                      </div>
                   </div>
                 ))
               )}
            </div>
         </div>

         <div className="bg-gray-900 p-10 rounded-[40px] shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-blue-600/5"></div>
            <div className="relative flex flex-col gap-10">
               <div className="flex items-center gap-3 text-blue-400">
                  <Globe className="h-6 w-6 animate-spin-slow" />
                  <h3 className="text-xl font-black text-white uppercase tracking-tighter">Global Footprint</h3>
               </div>
               <div className="w-full aspect-video bg-blue-950/40 rounded-[32px] border border-white/5 relative group-hover:scale-[1.01] transition-transform duration-700">
                  {salesHotspots.map(spot => (
                    <div key={spot.name} className="absolute group/spot" style={{ top: spot.top, left: spot.left }}>
                       <div className="w-4 h-4 bg-blue-600 rounded-full animate-ping absolute -inset-0"></div>
                       <div className="w-4 h-4 bg-blue-50 rounded-full relative shadow-lg shadow-blue-500/50 cursor-pointer"></div>
                    </div>
                  ))}
               </div>
            </div>
         </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
